import { TenantHelpers } from "@/utils/tenant-helpers";
import auth0 from "auth0-js";
import { EventEmitter } from "events";
import authConfig from "../../auth_config.json";
import { AppHelper } from "@/utils/app-helper";

const webAuth = new auth0.WebAuth({
  audience: authConfig.audience,
  clientID: authConfig.clientId,
  domain: authConfig.domain,
  redirectUri: `${window.location.origin}/callback`,
  responseType: "token id_token",
  scope: "openid profile email",
});

const localStorageKey = "loggedIn";
const loginEvent = "loginEvent";

class AuthService extends EventEmitter {
  idToken = null;
  profile = null;
  tokenExpiry = null;
  accessToken = null;
  accessTokenExpiry = null;
  tenants = null;
  uid = null;
  audience = authConfig.audience;
  webDomain = authConfig.webDomain;
  dashboardEnv = authConfig.dashboardEnv;
  slackWebhook = authConfig.slackWebhook;
  hubspotId = authConfig.hubspotId;

  login(customState, redirectUri) {
    // An alternative to login with page nav is using
    // webAuth.popup.authorize() https://auth0.com/docs/libraries/auth0js/v9#webauth-popup-authorize-
    // I found to be a really bad and weird ux because it will pop up in a different window.
    // If you want a modal instead, only lock.js offers that so we'd have to switch.
    if (redirectUri) {
      webAuth.authorize({
        appState: customState,
        redirectUri: redirectUri,
        allowSignUp: false,
      });
    } else {
      webAuth.authorize({
        appState: customState,
        allowSignUp: false,
      });
    }
  }

  logOut(customReturnTo) {
    localStorage.removeItem(localStorageKey);

    this.idToken = null;
    this.tokenExpiry = null;
    this.profile = null;
    this.accessToken = null;
    this.accessTokenExpiry = null;
    webAuth.logout({
      returnTo: `${window.location.origin}` + (customReturnTo ? customReturnTo : ''),
    });
    consoleLog("Emitting loginEvent from logOut");
    this.emit(loginEvent, { loggedIn: false });
  }

  handleAuthentication() {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) {
          reject(err);
        } else {
          this.localLogin(authResult);
          resolve(authResult.idToken);
        }
      });
    });
  }

  isAuthenticated() {
    return (
      Date.now() < this.tokenExpiry &&
      localStorage.getItem(localStorageKey) === "true"
    );
  }

  logOutIfPreviouslyLoggedIn(customReturnTo) {
    // Auth0 stores some id's in the cookie referring to the logged in user.
    // If you navigate to a page with a change in the url, vue will re-load the spa and
    // localStorageKey will be "true" but any local vars like this.tokenExpiry will be null.
    // That's fine except when you ask a user to login then (because locally we don't have their token)
    // it will skip the login because the auth0 cookie retains some session data about the last logged in user, and uses it.
    // So we need to ask auth0 to delete that cookie in the case we want the next login to be fresh. By calling logout().

    if (this.wasPreviouslyLoggedIn()) {
      this.logOut(customReturnTo);
      return true;
    }
    return false;
  }

  wasPreviouslyLoggedIn() {
    return localStorage.getItem(localStorageKey) === "true" && (this.tokenExpiry === undefined || this.tokenExpiry === null)
  }

  isIdTokenValid() {
    return (
      this.idToken &&
      this.tokenExpiry &&
      Date.now() < this.tokenExpiry
    );
  }

  getIdToken() {
    return new Promise((resolve, reject) => {
      if (this.isIdTokenValid()) {
        resolve(this.idToken);
      } else if (this.isAuthenticated()) {
        this.renewTokens().then(authResult => {
          resolve(authResult.idToken);
        }, reject);
      } else {
        resolve();
      }
    });
  }

  localLogin(authResult) {
    this.idToken = authResult.idToken;
    this.profile = authResult.idTokenPayload;

    // Convert the expiry time from seconds to milliseconds,
    // required by the Date constructor
    this.tokenExpiry = new Date(this.profile.exp * 1000);

    this.accessToken = authResult.accessToken;
    // Convert expiresIn to milliseconds and add the current time
    // (expiresIn is a relative timestamp, but an absolute time is desired)
    this.accessTokenExpiry = new Date(Date.now() + authResult.expiresIn * 1000);

    this.tenants = this.profile["https://jig.space/jigtenants"];
    this.uid = this.profile["https://jig.space/jiguserid"];

    localStorage.setItem(localStorageKey, "true");
    consoleLog("Emitting loginEvent from localLogin");
    this.emit(loginEvent, {
      loggedIn: true,
      profile: this.profile,
      state: authResult.appState || {},
      tenants: this.tenants,
      uid: this.uid,
    });
  }

  renewTokens() {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem(localStorageKey) !== "true") {
        return reject("Not logged in");
      }

      webAuth.checkSession({}, (err, authResult) => {
        if (err) {
          // As per auth0's documentation, calling renewTokens() should work if you're logged in, but I kept getting an error "login_required" so I gave up
          // but this is worth investigating in the future:
          // Here's an example: // code: "login_required" // description: "Login required" // error: "login_required" // error_description: "Login required"
          // and this one:
          // {"error":"timeout","error_description":"Timeout during executing web_message communication"},"code":"timeout","description":"Timeout during executing web_message communication","error":"timeout","error_description":"Timeout during executing web_message communication"}
          reject(err);
        } else {
          this.localLogin(authResult);
          resolve(authResult);
        }
      });
    });
  }

  isAccessTokenValid() {
    return (
      this.accessToken &&
      this.accessTokenExpiry &&
      Date.now() < this.accessTokenExpiry
    );
  }

  getAccessToken() {
    return new Promise((resolve, reject) => {
      if (this.isAccessTokenValid()) {
        resolve(this.accessToken);
      } else {
        this.renewTokens().then(authResult => {
          resolve(authResult.accessToken);
        }, reject);
      }
    });
  }

  /**
   * Identity tracking function of segment, usually be called
   * right after successful login event to track user details
   *
   * @typedef {Object} AuthData
   * @property {AuthProfileData} profile - audio error code
   * @property {array} tenants - array of user tenants
   * @property {string} message - error message (optional)
   *
   * @typedef {Object} AuthProfileData
   * @property {string} uid - Jig user id
   * @property {string} email - user email
   * @property {string} sub - a string contains signed up platform
   * @property {string} given_name (optional) - exists when signup with google or facebook
   * @property {string} first_name (optional) - exists when signup with apple
   * @property {string} family_name (optional) - exists when signup with google or facebook
   * @property {string} last_name (optional) - exists when signup with apple
   *
   * @param {AuthData} data - auth data
   * @param {array} tenants - array of user tenants
   * @param {Object} extraProperties - any other values that needs to be sent to segment
   */
  segmentIdentityTracking(tenants, currentTenant, extraProperties = {}) {
    // users signed up using google will have given_name and family_name fields
    // users signed up using apple account will have first_name and last_name fields.
    // Users signed up using facebook account will have given_name and family_name fields
    // users signed up using auth have no name fields.
    // profile.sub contains the signed up platform, examples:
    // google-oauth2|113412637137292723975
    // facebook|10157395758925701
    // apple|123456789
    // auth0|5c8748555a25970fe7bd8a67

    const signedupPlatform = this.profile.sub.split('|')[0]
    let firstName = ''
    let lastName = ''
    let fullname = ''
    let authProvider = 'JigSpaceDB'
    let currentTenantName = `${currentTenant.Name} (id: ${currentTenant.ID})`
    let activeTenantNames = ''

    switch (signedupPlatform) {
      case 'google-oauth2':
        firstName = this.profile.given_name
        lastName = this.profile.family_name
        fullname = `${firstName} ${lastName}`
        authProvider = 'Google'
        break;
      case 'apple':
        firstName = this.profile.first_name
        lastName = this.profile.last_name
        fullname = `${firstName} ${lastName}`
        authProvider = 'Apple'
        break;
      case 'facebook':
        firstName = this.profile.given_name
        lastName = this.profile.family_name
        fullname = `${firstName} ${lastName}`
        authProvider = 'Facebook'
        break;
      default:
        break;
    }

    if (TenantHelpers.IsSuperUserOrJigStaff()) {
      // Jig staff have access to every tenant and we don't want to list all of them in a massive identify call.
      activeTenantNames = 'JigSpaceStaff'
    } else {
      const activeTenants = tenants.filter(
        (t) => t.Active
      )
      const devTestTenantRegx = /^(Test)[a-zA-Z]+_20[\s0-9-:.+a-zA-Z]+m=[0-9+.]+$/

      if (activeTenants.length) {
        // tenant name is not unique, need to pass in ID which is the unique identifier
        activeTenantNames = activeTenants
          .map((t) => `${t.Name.replace(devTestTenantRegx, 'TestTenant')} (id: ${t.ID})`)
          .join(',')
      } else {
        activeTenantNames = this.tenants.map((t) => `${t.name.replace(devTestTenantRegx, 'TestTenant')} (id: ${t.id})`).join(',')
      }
    }

    // By default, each of the segment scripts (for dashboard, webflow, buy page) is setting a different anonymous id.
    // So if someone is logged in dashboard, but then goes to webflow, it is not recognising that person using the native Segment id method.
    // We can set the anonymous ID if it already exists to avoid this issue.
    // Details: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/
    //
    // We can pass in anonymouseId as option to both identity and track function and set its value using `analytics.user().anonymousId()`
    // analytics.user().anonymousId() returns user's current `anonymousId`
    // If user’s anonymousId is null (meaning not set), it automatically generats new one and sets it for the user.
    const option = this.getTrackingAnonymousId()

    // Change to return a promise so on logging out we can properly wait until tracking data has been sent.
    // Otherwise event can not be sent out successfully when user has already been logged out.
    return new Promise((resolve, reject) => {
      try {
        // consoleLog(`
        //   window.analytics.identify(
        //     ${this.profile.email},
        //     {
        //       ...this.getFieldIfExists('firstName', ${firstName}),
        //       ...this.getFieldIfExists('lastName', ${lastName}),
        //       ...this.getFieldIfExists('name', ${fullname.trim()}),
        //       email: ${this.profile.email},
        //       jigUserId: ${this.uid},
        //       tenantName: ${currentTenantName},
        //       ${activeTenantNames},
        //       ${authProvider},
        //       Subscription: ${AppHelper.getTierType(currentTenant.Subscription.TierType) || ''},
        //       ...${JSON.stringify(extraProperties)},
        //     },
        //     ${JSON.stringify(option)},
        //   )
        // `)

        window.analytics.identify(
          this.profile.email,
          {
            ...this.getFieldIfExists('firstName', firstName),
            ...this.getFieldIfExists('lastName', lastName),
            ...this.getFieldIfExists('name', fullname.trim()),
            email: this.profile.email,
            jigUserId: this.uid,
            tenantName: currentTenantName,
            activeTenantNames,
            authProvider,
            Subscription: AppHelper.getTierType(currentTenant.Subscription.TierType || ''),
            ...extraProperties,
          },
          option,
        )

        resolve(true)
      } catch (error) {
        consoleLog(`Error happened trying to push segment identify event: ${error}`)
        reject(false)
      }
    })
  }

  /**
   * Segment event tracking function
   * At this stage we try map data passed in the same as
   * gtm event tracking data.
   *
   * @typedef {Object} gtmEventData
   * @property {string} target
   * @property {string} action
   * @property {string} `target-properties`
   * @property {string} email (optional)
   * @property {string} role (optional)
   * @property {string} extraPermissions (optional)
   * @property {string} fileName (optional)
   * @property {string} editorEmail (optional)
   * @property {string} viewersEmails (optional)
   *
   * @param {gtmEventData} data - any data that needs tracking, map gtm event data
   */
  segmentEventTracking(event, data) {
    // See `segmentIdentityTracking()` function for details regarding `anonymousId`
    const option = this.getTrackingAnonymousId()

    try {
      // consoleLog(`
      //   window.analytics.track(
      //     ${event},
      //     {
      //       Application: 'dashboard',
      //       ...${JSON.stringify(data)},
      //       ...${this.getEmailIfExists()},
      //     },
      //     ${JSON.stringify(option)},
      //   )
      // `)
      window.analytics.track(
        event,
        {
          Application: 'dashboard',
          ...data,
          ...this.getEmailIfExists(),
        },
        option,
      )
    } catch (error) {
      consoleLog(`Error happened trying to push segment tracking event, ${error}`)
      return false
    }
  }

  getTrackingAnonymousId() {
    const option = {};

    // Check user function exists to prevent the error from adBlock.
    if (typeof window.analytics.user === 'function' && typeof window.analytics.user().anonymousId === 'function') {
      option.anonymousId = window.analytics.user().anonymousId();
    }

    return option;
  }

  getEmailIfExists(key) {
    // Only append email details to tracking event if it's available.
    return (
      this.profile &&
      this.profile.email && { [key || 'email']: this.profile.email }
    )
  }

  getFieldIfExists(key, val) {
    // Only append email details to tracking event if it's available.
    return (
      val && val !== '' && { [key]: val }
    )
  }
}

const service = new AuthService();

service.setMaxListeners(5);

export default service;
